<template>
  <div class="course-manage">
    <div class="page-header">
      <h2>网课商品管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
        添加商品
      </el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="商品标题">
          <el-input
            v-model="searchForm.title"
            placeholder="请输入商品标题"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select
            v-model="searchForm.status"
            placeholder="请选择状态"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="null" />
            <el-option label="草稿" :value="0" />
            <el-option label="上架" :value="1" />
            <el-option label="下架" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 380px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 表格 -->
    <div class="table-container">
      <el-table
        v-loading="loading"
        :data="goodsList"
        border
        stripe
        style="width: 100%"
        empty-text="暂无数据"
      >
        <el-table-column
          label="序号"
          width="80"
          align="center"
        >
          <template slot-scope="scope">
            {{ (pagination.current - 1) * pagination.size + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          label="封面图"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <img
              v-if="scope.row.coverImage"
              :src="scope.row.coverImage"
              class="goods-image"
              alt="封面图"
              @error="handleImageError"
            />
            <span v-else class="no-image">无图片</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="title"
          label="标题"
          min-width="150"
          show-overflow-tooltip
        />
        <el-table-column
          label="描述"
          min-width="200"
        >
          <template slot-scope="scope">
            <el-popover
              v-if="scope.row.description"
              placement="top"
              width="300"
              trigger="hover"
            >
              <p>{{ scope.row.description }}</p>
              <span slot="reference" class="description-text">
                {{ scope.row.description.length > 30 ? scope.row.description.substring(0, 30) + '...' : scope.row.description }}
              </span>
            </el-popover>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          label="整套课程价格"
          width="130"
          align="center"
        >
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.fullCoursePrice) }}
          </template>
        </el-table-column>
        <el-table-column
          label="促销价"
          width="130"
          align="center"
        >
          <template slot-scope="scope">
            <span v-if="scope.row.salePrice">¥{{ formatPrice(scope.row.salePrice) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          label="状态"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="推荐"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <el-switch
              :value="scope.row.recommend || false"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="(val) => handleRecommendChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          width="180"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="280"
          align="center"
          fixed="right"
        >
          <template slot-scope="scope">
            <div class="operation-buttons">
              <el-button
                type="primary"
                size="mini"
                icon="el-icon-edit"
                @click="handleEdit(scope.row)"
              >
                编辑
              </el-button>
              <el-select
                :value="scope.row.status"
                size="mini"
                @change="(val) => handleStatusChange(scope.row, val)"
                style="width: 100px"
              >
                <el-option label="草稿" :value="0" />
                <el-option label="上架" :value="1" />
                <el-option label="下架" :value="2" />
              </el-select>
              <el-button
                type="danger"
                size="mini"
                icon="el-icon-delete"
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        v-if="pagination.total > 0"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.size"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="1000px"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form
        ref="goodsForm"
        :model="goodsForm"
        :rules="formRules"
        label-width="120px"
      >
        <el-form-item label="商品标题" prop="title">
          <el-input
            v-model="goodsForm.title"
            placeholder="请输入商品标题"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="商品描述" prop="description">
          <el-input
            v-model="goodsForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入商品描述"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
        
        <!-- 图片上传区域 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="封面图" prop="coverImage">
              <div class="images-upload-section">
                <el-upload
                  class="cover-uploader"
                  :action="''"
                  :file-list="coverImageList"
                  :auto-upload="false"
                  :on-remove="handleRemoveCoverImage"
                  :on-change="handleCoverImageChange"
                  :before-upload="beforeImageUpload"
                  :limit="1"
                  list-type="picture-card"
                  accept="image/*"
                >
                  <i class="el-icon-plus"></i>
                </el-upload>
                <div class="image-tips">
                  <p>仅支持上传一张封面图</p>
                  <p style="color: #67C23A;">✓ 无大小限制，自动上传到腾讯云COS</p>
                </div>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="详情图片" prop="detailImages">
              <div class="images-upload-section">
                <el-upload
                  class="images-uploader"
                  :action="''"
                  :file-list="detailImageList"
                  :auto-upload="false"
                  :on-remove="handleRemoveImage"
                  :on-change="handleImageChange"
                  :before-upload="beforeImageUpload"
                  list-type="picture-card"
                  accept="image/*"
                  multiple
                >
                  <i class="el-icon-plus"></i>
                </el-upload>
                <div class="image-tips">
                  <p>支持多图上传</p>
                  <p style="color: #67C23A;">✓ 无大小限制，自动上传到腾讯云COS</p>
                </div>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 价格区域 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="整套课程价格（元）" prop="fullCoursePrice">
              <el-input-number
                v-model="goodsForm.fullCoursePrice"
                :precision="2"
                :min="0"
                :step="0.01"
                style="width: 100%"
                placeholder="请输入整套课程价格"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="促销价（元）" prop="salePrice">
              <el-input-number
                v-model="goodsForm.salePrice"
                :precision="2"
                :min="0"
                :step="0.01"
                style="width: 100%"
                placeholder="请输入促销价（可选）"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="章节列表" prop="chapters">
          <div class="chapters-section">
            <div
              v-for="(chapter, index) in goodsForm.chapters"
              :key="index"
              class="chapter-item"
            >
              <el-card shadow="hover" class="chapter-card">
                <div slot="header" class="chapter-header">
                  <span>章节 {{ index + 1 }}</span>
                  <el-button
                    type="danger"
                    size="mini"
                    icon="el-icon-delete"
                    @click="handleRemoveChapter(index)"
                  >
                    删除
                  </el-button>
                </div>
                <el-form :model="chapter" label-width="110px">
                  <el-row :gutter="20">
                    <el-col :span="12">
                      <el-form-item
                        label="章节序号"
                        :prop="'chapters.' + index + '.chapterSeq'"
                        :rules="{ required: true, message: '请输入章节序号', trigger: 'blur' }"
                      >
                        <el-input-number
                          v-model="chapter.chapterSeq"
                          :min="1"
                          placeholder="章节序号"
                          style="width: 100%"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item
                        label="章节标题"
                        :prop="'chapters.' + index + '.chapterTitle'"
                        :rules="{ required: true, message: '请输入章节标题', trigger: 'blur' }"
                      >
                        <el-input
                          v-model="chapter.chapterTitle"
                          placeholder="请输入章节标题"
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                  <el-form-item label="章节描述">
                    <el-input
                      v-model="chapter.chapterDesc"
                      type="textarea"
                      :rows="2"
                      placeholder="请输入章节描述（可选）"
                    />
                  </el-form-item>
                  
                  <!-- 价格和免费设置 -->
                  <el-row :gutter="20">
                    <el-col :span="16">
                      <el-form-item
                        label="章节价格（元）"
                        :prop="'chapters.' + index + '.specPrice'"
                        :rules="{ required: true, message: '请输入章节价格', trigger: 'blur' }"
                      >
                        <div class="price-input-wrapper">
                          <el-input-number
                            v-model="chapter.specPrice"
                            :precision="2"
                            :min="0"
                            :step="0.01"
                            :disabled="chapter.isFree || chapter.enablePriceCalculation"
                            style="width: 200px"
                            placeholder="章节价格"
                          />
                          <div class="price-controls">
                            <el-switch
                              v-model="chapter.enablePriceCalculation"
                              active-text="自动计算"
                              inactive-text="手动输入"
                              @change="handlePriceCalculationChange(index)"
                            />
                            <el-tooltip content="启用后，系统会根据总价格和章节数量自动计算每个章节的价格。免费章节不纳入计算。例如：总价100元，共11章，每章约9.09元；如果1章免费，则剩余10章每章10元。" placement="top">
                              <i class="el-icon-question price-help-icon"></i>
                            </el-tooltip>
                          </div>
                        </div>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="是否免费">
                        <el-switch
                          v-model="chapter.isFree"
                          active-text="免费"
                          inactive-text="付费"
                          @change="handleFreeChange(index)"
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <!-- 视频上传 -->                  <el-form-item label="视频/文件">
                    <div class="video-upload-wrapper">
                      <el-upload
                        :action="''"
                        :file-list="chapter.fileList || []"
                        :auto-upload="false"
                        :on-change="(file, fileList) => handleVideoChange(index, file, fileList)"
                        :on-remove="() => handleVideoRemove(index)"
                        :before-upload="beforeVideoUpload"
                        :limit="1"
                        accept="*/*"
                      >
                        <el-button size="small" type="primary" icon="el-icon-upload2">上传文件</el-button>
                      </el-upload>
                      <el-tooltip content="支持视频或压缩包，无大小限制，自动分块上传到腾讯云COS" placement="top">
                        <i class="el-icon-question video-help-icon"></i>
                      </el-tooltip>
                    </div>

                    <!-- 上传进度显示 -->
                    <div v-if="chapter.uploadProgress && chapter.uploadProgress.uploading" class="upload-progress-card">
                      <div class="progress-header">
                        <span class="progress-title">
                          <i class="el-icon-upload2"></i>
                          正在上传: {{ chapter.uploadProgress.fileName }}
                        </span>
                        <span class="progress-percent">{{ chapter.uploadProgress.percent }}%</span>
                      </div>
                      <el-progress
                        :percentage="chapter.uploadProgress.percent"
                        :stroke-width="18"
                        :status="chapter.uploadProgress.status"
                      ></el-progress>
                      <div class="progress-details">
                        <span class="upload-speed" v-if="chapter.uploadProgress.speed">
                          <i class="el-icon-upload"></i> {{ formatSpeed(chapter.uploadProgress.speed) }}
                        </span>
                        <span class="upload-size">
                          {{ formatFileSize(chapter.uploadProgress.loaded) }} / {{ formatFileSize(chapter.uploadProgress.total) }}
                        </span>
                        <span v-if="chapter.uploadProgress.remainingTime" class="remaining-time">
                          <i class="el-icon-time"></i> 剩余 {{ formatTime(chapter.uploadProgress.remainingTime) }}
                        </span>
                      </div>
                    </div>

                    <!-- 已上传文件信息卡片 -->
                    <div v-if="chapter.videoUrl && (!chapter.uploadProgress || !chapter.uploadProgress.uploading)" class="uploaded-file-card">
                      <div class="file-card-content">
                        <div class="file-icon-wrapper">
                          <i :class="getFileIcon(chapter.videoUrl)" class="file-icon"></i>
                        </div>
                        <div class="file-info">
                          <div class="file-name" :title="getFileName(chapter.videoUrl)">
                            {{ truncateFileName(getFileName(chapter.videoUrl), 30) }}
                          </div>
                          <div class="file-meta">
                            <span class="file-type">{{ getFileType(chapter.videoUrl) }}</span>
                            <span class="file-separator">•</span>
                            <span class="file-status">
                              <i class="el-icon-success"></i>
                              已上传
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="file-actions">
                        <el-button
                          type="text"
                          size="small"
                          icon="el-icon-download"
                          @click="handleDownloadFile(chapter.videoUrl)"
                        >
                          下载
                        </el-button>
                        <el-button
                          type="text"
                          size="small"
                          icon="el-icon-refresh"
                          @click="handleReplaceFile(index)"
                        >
                          替换
                        </el-button>
                        <el-button
                          type="text"
                          size="small"
                          icon="el-icon-delete"
                          @click="handleClearFile(index)"
                        >
                          清除
                        </el-button>
                      </div>
                    </div>
                  </el-form-item>
                </el-form>
              </el-card>
            </div>
            <el-button
              type="dashed"
              icon="el-icon-plus"
              style="width: 100%; margin-top: 10px;"
              @click="handleAddChapter"
            >
              添加章节
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getGoodsList, createGoods, getGoodsDetail, updateGoods, deleteGoods, updateGoodsStatus, toggleRecommend } from '@/api/goods'
import { uploadFile } from '@/utils/cosUpload'
import { formatDate, formatPrice, getFileName, truncateFileName, getFileType, getFileIcon, getDefaultImagePlaceholder } from '@/utils/index'
import { GOODS_STATUS_TEXT, GOODS_STATUS_TYPE } from '@/utils/constants'
import { handleApiError } from '@/utils/errorHandler'

export default {
  name: 'CourseManage',
  data() {
    return {
      loading: false,
      submitLoading: false,
      goodsList: [],
      searchForm: {
        title: '',
        status: null
      },
      dateRange: null,
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '添加商品',
      isEdit: false,
      goodsForm: {
        title: '',
        description: '',
        coverImage: '',
        detailImages: [],
        fullCoursePrice: 0,
        salePrice: null,
            chapters: []
          },
          enablePriceCalculation: false,
      formRules: {
        title: [
          { required: true, message: '请输入商品标题', trigger: 'blur' }
        ],
        fullCoursePrice: [
          { required: true, message: '请输入整套课程价格', trigger: 'blur' },
          { type: 'number', min: 0, message: '价格必须大于等于0', trigger: 'blur' }
        ],
        chapters: [
          { required: true, type: 'array', min: 1, message: '至少需要添加一个章节', trigger: 'change' }
        ]
      },
      detailImageList: [],
      coverImageList: [],
      uploadedImageUrls: [],
      uploadingImages: []
    }
  },
  mounted() {
    this.loadGoodsList()
  },
  watch: {
    // 监听总价格变化，重新计算启用价格计算的章节
    'goodsForm.fullCoursePrice'() {
      // 只有在有章节启用价格计算时才重新计算
      const hasEnabledCalculation = this.goodsForm.chapters.some(
        ch => ch.enablePriceCalculation && !ch.isFree
      )
      if (hasEnabledCalculation) {
        this.$nextTick(() => {
          this.recalculateAllChapterPrices()
        })
      }
    },
    // 监听章节数量变化，重新计算启用价格计算的章节
    'goodsForm.chapters': {
      handler(newVal) {
        // 只有在有章节启用价格计算时才重新计算
        // 避免在编辑时加载数据时触发
        const hasEnabledCalculation = newVal.some(
          ch => ch.enablePriceCalculation && !ch.isFree
        )
        if (hasEnabledCalculation) {
          this.$nextTick(() => {
            this.recalculateAllChapterPrices()
          })
        }
      },
      deep: true
    }
  },
  methods: {
    // 加载商品列表
    async loadGoodsList() {
      this.loading = true
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          goodsType: 1, // 商品类型：1-课程
          title: this.searchForm.title,
          status: this.searchForm.status
        }
        
        // 处理时间范围
        if (this.dateRange && this.dateRange.length === 2) {
          params.startTime = this.dateRange[0]
          params.endTime = this.dateRange[1]
        }
        
        const response = await getGoodsList(params)
        if (response.data && response.data.success) {
          this.goodsList = response.data.data.records || []
          this.pagination.total = response.data.data.total || 0
        } else {
          this.$message.error(response.data?.message || '获取商品列表失败')
        }
      } catch (error) {
        handleApiError(error, '加载商品列表失败', { showMessage: true })
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadGoodsList()
    },
    
    // 重置搜索
    handleReset() {
      this.searchForm = {
        title: '',
        status: null
      }
      this.dateRange = null
      this.pagination.current = 1
      this.loadGoodsList()
    },
    
    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.loadGoodsList()
    },
    
    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadGoodsList()
    },
    
    // 格式化价格（分转元）- 使用工具函数
    formatPrice,
    
    // 格式化日期 - 使用工具函数
    formatDate,
    
    // 获取状态文本
    getStatusText(status) {
      return GOODS_STATUS_TEXT[status] || '未知'
    },
    
    // 获取状态类型（用于标签颜色）
    getStatusType(status) {
      return GOODS_STATUS_TYPE[status] || ''
    },
    
    // 图片加载错误处理
    handleImageError(e) {
      e.target.src = getDefaultImagePlaceholder()
    },
    
    // 添加商品
    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '添加商品'
      this.resetForm()
      this.dialogVisible = true
    },
    
    // 编辑商品
    async handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑商品'
      this.loading = true
      try {
        const response = await getGoodsDetail(row.id)
        if (response.data && response.data.success) {
          const goods = response.data.data
          this.goodsForm = {
            id: goods.id,
            title: goods.title || '',
            description: goods.description || '',
            coverImage: goods.coverImage || '',
            detailImages: goods.detailImages || [],
            fullCoursePrice: goods.fullCoursePrice ? goods.fullCoursePrice / 100 : 0,
            salePrice: goods.salePrice ? goods.salePrice / 100 : null,
            chapters: ((goods.specsList || goods.chapters || [])
              .filter(ch => ch.chapterSeq !== 0) // 过滤掉"整套课程"项
              .map(ch => ({
                chapterSeq: ch.chapterSeq || 1,
                chapterTitle: ch.chapterTitle || '',
                chapterDesc: ch.chapterDesc || '',
                specPrice: ch.specPrice ? ch.specPrice / 100 : 0,
                isFree: ch.isFree || false,
                videoUrl: ch.videoUrl || '',
                fileList: ch.videoUrl ? [{ name: ch.videoUrl.split('/').pop(), url: ch.videoUrl }] : [],
                enablePriceCalculation: false
              }))
              .sort((a, b) => a.chapterSeq - b.chapterSeq)) // 按章节序号排序
          }
          
          // 设置详情图片列表
          this.uploadedImageUrls = goods.detailImages || []
          this.detailImageList = (goods.detailImages || []).map((url, index) => ({
            uid: index,
            name: `图片${index + 1}`,
            url: url,
            status: 'success'
          }))
          
          // 设置封面图片列表
          if (goods.coverImage) {
            this.coverImageList = [{
              uid: -1,
              name: '封面图',
              url: goods.coverImage,
              status: 'success'
            }]
          } else {
            this.coverImageList = []
          }
          
          this.dialogVisible = true
        } else {
          this.$message.error(response.data?.message || '获取商品详情失败')
        }
      } catch (error) {
        handleApiError(error, '加载商品详情失败', { showMessage: true })
      } finally {
        this.loading = false
      }
    },
    
    // 删除商品
    handleDelete(row) {
      this.$confirm('确定要删除该商品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteGoods(row.id)
          if (response.data && response.data.success) {
            this.$message.success('删除成功')
            this.loadGoodsList()
          } else {
            this.$message.error(response.data?.message || '删除失败')
          }
        } catch (error) {
          handleApiError(error, '删除失败', { showMessage: true })
        }
      }).catch(() => {})
    },
    
    // 状态管理
    handleStatusChange(row, newStatus) {
      if (newStatus === row.status) {
        return
      }
      
      this.$confirm(`确定要将状态修改为${newStatus === 0 ? '草稿' : newStatus === 1 ? '上架' : '下架'}吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await updateGoodsStatus(row.id, newStatus)
          if (response.data && response.data.success) {
            this.$message.success('状态修改成功')
            this.loadGoodsList()
          } else {
            this.$message.error(response.data?.message || '状态修改失败')
          }
        } catch (error) {
          handleApiError(error, '修改状态失败', { showMessage: true })
        }
      }).catch(() => {
        // 取消时恢复原值
        this.$forceUpdate()
      })
    },
    
    // 切换推荐状态
    async handleRecommendChange(row, newRecommend) {
      // 保存原值，用于失败时恢复
      const oldRecommend = row.recommend || false
      // 先更新本地数据，提供即时反馈
      row.recommend = newRecommend
      
      try {
        const response = await toggleRecommend(row.id)
        if (response.data && response.data.success) {
          this.$message.success(newRecommend ? '已设为推荐' : '已取消推荐')
        } else {
          // 失败时恢复原值
          row.recommend = oldRecommend
          this.$message.error(response.data?.message || '切换推荐状态失败')
        }
      } catch (error) {
        // 失败时恢复原值
        row.recommend = oldRecommend
        handleApiError(error, '切换推荐状态失败', { showMessage: true })
      }
    },
    
    // 图片上传前的验证
    beforeImageUpload() {
      // 不限制大小，支持超大图片
      return false // 阻止自动上传
    },

    // 图片变化处理 - 选择后立即上传到COS
    async handleImageChange(file, fileList) {
      // 如果是新添加的文件（有raw属性），立即上传
      if (file.raw && file.status === 'ready') {
        try {
          // 显示上传中状态
          file.status = 'uploading'
          this.detailImageList = [...fileList]
          this.$message.info('正在上传图片到腾讯云COS...')

          // 调用COS上传接口
          const result = await uploadFile({
            file: file.raw,
            folder: 'images/goods/',
            onProgress: (progressData) => {
              console.log('图片上传进度:', progressData.percent + '%')
            }
          })

          if (result && result.url) {
            // 上传成功，更新文件状态和URL
            file.status = 'success'
            file.url = result.url
            // 添加到已上传URL列表
            this.uploadedImageUrls.push(result.url)
            this.$message.success('图片上传成功')
          }
        } catch (error) {
          // 上传失败，移除该文件
          file.status = 'fail'
          this.$message.error('图片上传失败：' + (error.message || '未知错误'))
          // 从列表中移除失败的文件
          const index = fileList.indexOf(file)
          if (index > -1) {
            fileList.splice(index, 1)
          }
        }
      }
      this.detailImageList = fileList
    },
    
    // 移除图片
    handleRemoveImage(file, fileList) {
      this.detailImageList = fileList
      // 从已上传的URL列表中移除
      if (file.url) {
        this.uploadedImageUrls = this.uploadedImageUrls.filter(url => url !== file.url)
      }
    },
    
    // 封面图变化处理 - 选择后立即上传到COS
    async handleCoverImageChange(file, fileList) {
      // 如果是新添加的文件（有raw属性），立即上传
      if (file.raw && file.status === 'ready') {
        try {
          // 显示上传中状态
          file.status = 'uploading'
          this.coverImageList = [...fileList]
          this.$message.info('正在上传封面图到腾讯云COS...')

          // 调用COS上传接口
          const result = await uploadFile({
            file: file.raw,
            folder: 'images/goods/cover/',
            onProgress: (progressData) => {
              console.log('封面图上传进度:', progressData.percent + '%')
            }
          })

          if (result && result.url) {
            // 上传成功，更新文件状态和URL
            file.status = 'success'
            file.url = result.url
            // 更新表单中的封面图URL
            this.goodsForm.coverImage = result.url
            this.$message.success('封面图上传成功')
          }
        } catch (error) {
          // 上传失败，移除该文件
          file.status = 'fail'
          this.$message.error('封面图上传失败：' + (error.message || '未知错误'))
          // 从列表中移除失败的文件
          const index = fileList.indexOf(file)
          if (index > -1) {
            fileList.splice(index, 1)
          }
        }
      }
      this.coverImageList = fileList
    },
    
    // 移除封面图
    handleRemoveCoverImage(file, fileList) {
      this.coverImageList = fileList
      this.goodsForm.coverImage = ''
    },
    
    // 视频上传前的验证
    beforeVideoUpload() {
      // 不自动上传，返回false
      return false
    },
    
    // 视频文件变化处理 - 选择后立即上传到COS
    async handleVideoChange(chapterIndex, file, fileList) {
      if (fileList.length > 0) {
        const latestFile = fileList[fileList.length - 1]
        this.goodsForm.chapters[chapterIndex].fileList = [latestFile]

        // 如果是新添加的文件（有raw属性），立即上传
        if (latestFile.raw && latestFile.status === 'ready') {
          try {
            // 初始化上传进度对象
            this.$set(this.goodsForm.chapters[chapterIndex], 'uploadProgress', {
              uploading: true,
              fileName: latestFile.name,
              percent: 0,
              loaded: 0,
              total: latestFile.size,
              speed: 0,
              remainingTime: 0,
              status: ''
            })

            // 显示上传中状态
            latestFile.status = 'uploading'
            this.$message.info('正在上传文件到腾讯云COS，请稍候...')

            const startTime = Date.now()
            let lastLoaded = 0
            let lastTime = startTime

            // 调用COS上传接口
            const result = await uploadFile({
              file: latestFile.raw,
              folder: 'videos/courses/',
              onProgress: (progressData) => {
                const progress = this.goodsForm.chapters[chapterIndex].uploadProgress
                progress.percent = progressData.percent
                progress.loaded = progressData.loaded
                progress.total = progressData.total
                progress.speed = progressData.speed

                // 计算剩余时间
                const currentTime = Date.now()
                const timeElapsed = (currentTime - lastTime) / 1000
                if (timeElapsed > 0.5) { // 每0.5秒更新一次
                  const bytesUploaded = progressData.loaded - lastLoaded
                  const currentSpeed = bytesUploaded / timeElapsed
                  const remainingBytes = progressData.total - progressData.loaded
                  progress.remainingTime = currentSpeed > 0 ? remainingBytes / currentSpeed : 0

                  lastLoaded = progressData.loaded
                  lastTime = currentTime
                }
              }
            })

            if (result && result.url) {
              // 上传成功，更新文件状态和URL
              latestFile.status = 'success'
              latestFile.url = result.url
              this.goodsForm.chapters[chapterIndex].videoUrl = result.url

              // 更新进度状态为成功
              this.goodsForm.chapters[chapterIndex].uploadProgress.uploading = false
              this.goodsForm.chapters[chapterIndex].uploadProgress.status = 'success'
              this.goodsForm.chapters[chapterIndex].uploadProgress.percent = 100

              this.$message.success('文件上传成功')

              // 延迟清除进度显示
              setTimeout(() => {
                this.$set(this.goodsForm.chapters[chapterIndex], 'uploadProgress', null)
              }, 2000)
            }
          } catch (error) {
            // 上传失败，清除该文件
            latestFile.status = 'fail'

            // 更新进度状态为失败
            if (this.goodsForm.chapters[chapterIndex].uploadProgress) {
              this.goodsForm.chapters[chapterIndex].uploadProgress.uploading = false
              this.goodsForm.chapters[chapterIndex].uploadProgress.status = 'exception'
            }

            this.$message.error('文件上传失败：' + (error.message || '未知错误'))

            // 清除失败的文件
            this.goodsForm.chapters[chapterIndex].fileList = []
            this.goodsForm.chapters[chapterIndex].videoUrl = ''

            // 延迟清除进度显示
            setTimeout(() => {
              this.$set(this.goodsForm.chapters[chapterIndex], 'uploadProgress', null)
            }, 3000)
          }
        }
      }
    },
    
    // 移除视频
    handleVideoRemove(chapterIndex) {
      this.goodsForm.chapters[chapterIndex].fileList = []
      this.goodsForm.chapters[chapterIndex].videoUrl = ''
    },
    
    // 获取文件名 - 使用工具函数
    getFileName,
    
    // 截取文件名 - 使用工具函数
    truncateFileName,
    
    // 获取文件类型 - 使用工具函数
    getFileType,
    
    // 获取文件图标 - 使用工具函数
    getFileIcon,
    
    // 下载文件
    handleDownloadFile(url) {
      if (!url) return
      try {
        const link = document.createElement('a')
        link.href = url
        link.download = getFileName(url)
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        handleApiError(error, '下载文件失败', { showMessage: true })
      }
    },
    
    // 替换文件
    handleReplaceFile(chapterIndex) {
      // 清除当前文件，允许重新上传
      this.goodsForm.chapters[chapterIndex].fileList = []
      this.goodsForm.chapters[chapterIndex].videoUrl = ''
      this.$message.info('请重新上传文件')
    },
    
    // 清除文件
    handleClearFile(chapterIndex) {
      this.$confirm('确定要清除已上传的文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.goodsForm.chapters[chapterIndex].fileList = []
        this.goodsForm.chapters[chapterIndex].videoUrl = ''
        this.$message.success('已清除文件')
      }).catch(() => {})
    },
    
    // 添加章节
    handleAddChapter() {
      this.goodsForm.chapters.push({
        chapterSeq: this.goodsForm.chapters.length + 1,
        chapterTitle: '',
        chapterDesc: '',
        specPrice: 0,
        isFree: false,
        videoUrl: '',
        fileList: [],
        enablePriceCalculation: false
      })
    },
    
    // 删除章节
    handleRemoveChapter(index) {
      this.goodsForm.chapters.splice(index, 1)
    },
    
    // 免费开关变化
    handleFreeChange(index) {
      if (this.goodsForm.chapters[index].isFree) {
        this.goodsForm.chapters[index].specPrice = 0
        this.goodsForm.chapters[index].enablePriceCalculation = false
      } else {
        // 如果不是免费，如果启用了价格计算，重新计算所有章节价格
        if (this.goodsForm.chapters[index].enablePriceCalculation) {
          this.recalculateAllChapterPrices()
        }
      }
    },
    
    // 价格计算开关变化
    handlePriceCalculationChange(index) {
      if (this.goodsForm.chapters[index].enablePriceCalculation) {
        // 当启用价格计算时，重新计算所有启用价格计算的章节
        this.recalculateAllChapterPrices()
      } else {
        // 关闭价格计算时，不清空价格，保持当前值
      }
    },
    
    // 重新计算所有启用价格计算的章节
    recalculateAllChapterPrices() {
      const totalPrice = this.goodsForm.fullCoursePrice || 0
      if (totalPrice <= 0) {
        return
      }
      
      // 计算启用价格计算且非免费的章节数量
      const chaptersToCalculate = this.goodsForm.chapters.filter(
        ch => ch.enablePriceCalculation && !ch.isFree
      )
      const count = chaptersToCalculate.length
      
      if (count > 0) {
        const pricePerChapter = totalPrice / count
        const calculatedPrice = parseFloat(pricePerChapter.toFixed(2))
        
        // 为所有启用价格计算的章节设置相同价格
        this.goodsForm.chapters.forEach((chapter) => {
          if (chapter.enablePriceCalculation && !chapter.isFree) {
            chapter.specPrice = calculatedPrice
          }
        })
      }
    },
    
    // 计算单个章节价格（已废弃，保留用于兼容）
    calculateChapterPrice() {
      // 统一使用 recalculateAllChapterPrices
      this.recalculateAllChapterPrices()
    },
    
    // 上传图片到COS（已改为选择时立即上传，此方法仅用于收集已上传的URL）
    async uploadImagesToCOS() {
      let coverUrl = ''
      let detailUrls = []

      // 获取封面图URL（已在选择时上传）
      if (this.coverImageList.length > 0 && this.coverImageList[0].url) {
        coverUrl = this.coverImageList[0].url
        this.goodsForm.coverImage = coverUrl
      }

      // 获取详情图URL列表（已在选择时上传）
      detailUrls = this.detailImageList
        .filter(file => file.url)
        .map(file => file.url)
      this.uploadedImageUrls = detailUrls

      return { detailUrls, coverUrl }
    },
    
    // 上传视频到COS（已改为选择时立即上传，此方法仅用于返回已上传的URL）
    async uploadVideoToCOS(chapter) {
      // 如果已经有videoUrl，直接返回
      if (chapter.videoUrl) {
        return chapter.videoUrl
      }

      // 如果fileList中有文件且有url，返回该url
      if (chapter.fileList && chapter.fileList.length > 0 && chapter.fileList[0].url) {
        return chapter.fileList[0].url
      }

      // 否则返回空字符串
      return ''
    },
    
    // 提交表单
    async handleSubmit() {
      this.$refs.goodsForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        // 验证章节
        if (!this.goodsForm.chapters || this.goodsForm.chapters.length === 0) {
          this.$message.error('至少需要添加一个章节')
          return false
        }
        
        // 验证每个章节的必填项
        for (let i = 0; i < this.goodsForm.chapters.length; i++) {
          const chapter = this.goodsForm.chapters[i]
          if (!chapter.chapterSeq || !chapter.chapterTitle) {
            this.$message.error(`章节 ${i + 1} 的序号和标题不能为空`)
            return false
          }
          if (!chapter.isFree && (!chapter.specPrice || chapter.specPrice <= 0)) {
            this.$message.error(`章节 ${i + 1} 的价格必须大于0（免费章节请开启免费开关）`)
            return false
          }
        }
        
        this.submitLoading = true
        try {
          // 上传图片（包括封面图和详情图）
          const imageResult = await this.uploadImagesToCOS()
          const imageUrls = imageResult.detailUrls || []
          const coverImage = imageResult.coverUrl || this.goodsForm.coverImage || ''
          
          // 如果没有图片URL但有已存在的图片（编辑模式）
          if (imageUrls.length === 0 && this.detailImageList.length > 0) {
            imageUrls.push(...this.detailImageList.map(f => f.url).filter(url => url))
          }
          
          // 准备章节数据（视频已在选择时上传）
          const chapters = []
          for (let i = 0; i < this.goodsForm.chapters.length; i++) {
            const chapter = this.goodsForm.chapters[i]
            // 直接使用已上传的videoUrl
            const videoUrl = chapter.videoUrl || ''

            chapters.push({
              chapterSeq: chapter.chapterSeq,
              chapterTitle: chapter.chapterTitle,
              chapterDesc: chapter.chapterDesc || '',
              specPrice: Math.round((chapter.specPrice || 0) * 100), // 元转分
              isFree: chapter.isFree || false,
              videoUrl: videoUrl
            })
          }
          
          // 准备提交数据
          const submitData = {
            title: this.goodsForm.title,
            description: this.goodsForm.description || '',
            detailImages: imageUrls,
            coverImage: coverImage,
            fullCoursePrice: Math.round((this.goodsForm.fullCoursePrice || 0) * 100), // 元转分
            salePrice: this.goodsForm.salePrice ? Math.round(this.goodsForm.salePrice * 100) : null, // 元转分
            chapters: chapters
          }
          
          let response
          if (this.isEdit) {
            response = await updateGoods(this.goodsForm.id, submitData)
          } else {
            response = await createGoods(submitData)
          }
          
          if (response.data && response.data.success) {
            this.$message.success(this.isEdit ? '更新成功' : '创建成功')
            this.dialogVisible = false
            this.loadGoodsList()
          } else {
            this.$message.error(response.data?.message || (this.isEdit ? '更新失败' : '创建失败'))
          }
        } catch (error) {
          handleApiError(error, '提交失败', { showMessage: true })
        } finally {
          this.submitLoading = false
        }
      })
    },
    
    // 重置表单
    resetForm() {
      this.goodsForm = {
        title: '',
        description: '',
        coverImage: '',
        detailImages: [],
        fullCoursePrice: 0,
        salePrice: null,
        chapters: []
      }
      this.detailImageList = []
      this.coverImageList = []
      this.uploadedImageUrls = []
      if (this.$refs.goodsForm) {
        this.$refs.goodsForm.clearValidate()
      }
    },
    
    // 对话框关闭
    handleDialogClose() {
      this.resetForm()
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
    },

    // 格式化上传速度
    formatSpeed(bytesPerSecond) {
      if (!bytesPerSecond || bytesPerSecond === 0) return '0 B/s'
      return this.formatFileSize(bytesPerSecond) + '/s'
    },

    // 格式化剩余时间
    formatTime(seconds) {
      if (!seconds || seconds === 0) return '0秒'
      if (seconds < 60) {
        return Math.round(seconds) + '秒'
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60)
        const secs = Math.round(seconds % 60)
        return `${minutes}分${secs}秒`
      } else {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        return `${hours}小时${minutes}分`
      }
    }
  }
}
</script>

<style scoped>
.course-manage {
  padding: 20px;
  height: 100%;
  overflow-y: auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.search-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.table-container {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.goods-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}

.no-image {
  color: #999;
  font-size: 12px;
}

.description-text {
  cursor: pointer;
  color: #409eff;
}

.operation-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
}

.images-upload-section {
  width: 100%;
}

.images-uploader {
  margin-bottom: 10px;
}

.image-tips {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.image-tips p {
  margin: 5px 0;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.chapters-section {
  width: 100%;
}

.chapter-item {
  margin-bottom: 15px;
}

.chapter-card {
  margin-bottom: 15px;
}

.chapter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.video-url {
  margin-top: 8px;
  font-size: 12px;
  color: #67c23a;
  word-break: break-all;
  padding: 8px;
  background: #f0f9ff;
  border-radius: 4px;
  border: 1px solid #b3d8ff;
}

.dialog-footer {
  text-align: right;
}

.price-input-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.price-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-help-icon {
  color: #909399;
  cursor: help;
  font-size: 16px;
  margin-left: 5px;
}

.video-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.video-help-icon {
  color: #909399;
  cursor: help;
  font-size: 16px;
}

.uploaded-file-card {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.uploaded-file-card:hover {
  background: #f0f2f5;
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.file-card-content {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.file-icon-wrapper {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-right: 15px;
  flex-shrink: 0;
}

.file-icon {
  font-size: 24px;
  color: #fff;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.file-type {
  padding: 2px 8px;
  background: #e4e7ed;
  border-radius: 3px;
  color: #606266;
}

.file-separator {
  color: #c0c4cc;
}

.file-status {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #67c23a;
}

.file-status i {
  font-size: 14px;
}

.file-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 15px;
}

.file-actions .el-button {
  padding: 5px 10px;
  font-size: 12px;
}

.file-actions .el-button--text {
  color: #606266;
}

.file-actions .el-button--text:hover {
  color: #409eff;
}

.chapter-card {
  margin-bottom: 15px;
}

.chapter-card .el-card__body {
  padding: 20px;
}

.chapters-section {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.chapters-section::-webkit-scrollbar {
  width: 6px;
}

.chapters-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chapters-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chapters-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 上传进度卡片样式 */
.upload-progress-card {
  margin-top: 15px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-title {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.progress-title i {
  margin-right: 5px;
  color: #409eff;
}

.progress-percent {
  font-size: 16px;
  font-weight: bold;
  color: #409eff;
}

.progress-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.upload-speed {
  color: #67c23a;
  font-weight: 500;
}

.upload-speed i {
  margin-right: 3px;
}

.upload-size {
  color: #606266;
}

.remaining-time {
  color: #e6a23c;
}

.remaining-time i {
  margin-right: 3px;
}
</style>
